import { useCallback, useEffect, useState } from 'react';
import { RefreshCcw } from 'lucide-react';
import { Button } from '@/shared/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/ui/tabs';
import Breadcrumb from '@/shared/components/Breadcrumb';
import {
  AGREGATEURS, getAgregateurs, getDivergences, getSante, getSynthese,
} from '@/shared/api/bridge';
import { alertesSante } from '@/features/bridge/bridge-etat';
import BandeauMigration from '@/features/bridge/components/BandeauMigration';
import ContratRaccordement from '@/features/bridge/components/ContratRaccordement';
import OngletAcces from '@/features/bridge/components/OngletAcces';
import OngletJournal from '@/features/bridge/components/OngletJournal';
import OngletPerimetre from '@/features/bridge/components/OngletPerimetre';
import OngletSante from '@/features/bridge/components/OngletSante';
import SelecteurAgregateur from '@/features/bridge/components/SelecteurAgregateur';
import VerdictRaccordement from '@/features/bridge/components/VerdictRaccordement';

/**
 * Le bridge agrégateurs — donner accès, couper l'accès, rendre des comptes.
 *
 * POURQUOI CET ÉCRAN EXISTE
 *
 * Survoltage et Symphonics demandaient tous deux un accès à notre projet Tuya. C'est
 * refusé : une clé projet donne TOUT le parc en écriture, sans réglage par appareil et
 * sans révocation possible sans couper Flexcore au même instant. Le bridge remplace
 * cette clé : nous gardons les nôtres, nous exécutons, nous répondons. Un bridge qui ne
 * se piloterait qu'en ligne de commande ne tiendrait pas sa promesse ; tous ses gestes
 * sont donc ici.
 *
 * L'ORDRE DE LA PAGE, REFONDU LE 28/09/2026
 *
 * Sept blocs étaient empilés dans l'ordre où ils avaient été construits, le formulaire
 * du contrat en tête. On lit désormais dans l'ordre où l'on se pose les questions :
 *
 *   1. reste-t-il des actifs à déclarer ?      aperçu, seulement s'il y a lieu
 *   2. cet agrégateur peut-il piloter ?          trois étapes, et qui doit agir
 *   3. le détail, par geste                      Périmètre · Accès · Journal ·
 *                                                Contrat · Santé
 *
 * Chaque onglet charge ses propres données et porte ses propres erreurs. Après un geste,
 * seul l'onglet touché se recharge, avec la synthèse qui le résume.
 */
export default function BridgePage() {
  const [agregateurs, setAgregateurs] = useState(AGREGATEURS);
  const [agregateur, setAgregateur] = useState('survoltage');
  const [onglet, setOnglet] = useState('perimetre');
  const [syntheses, setSyntheses] = useState({});
  const [erreurSynthese, setErreurSynthese] = useState(null);
  const [sante, setSante] = useState(null);
  const [divergences, setDivergences] = useState(null);
  const [erreurSante, setErreurSante] = useState(null);
  const [erreurDiv, setErreurDiv] = useState(null);
  // Incrémenté après une déclaration depuis l'aperçu : les onglets remontent et
  // relisent tout, le périmètre vient de changer hors de leur vue.
  const [generation, setGeneration] = useState(0);

  // La liste vient du serveur ; le repli à deux entrées reste affiché si elle tombe,
  // un écran vide laisserait croire qu'aucun agrégateur n'existe.
  useEffect(() => {
    let vivant = true;
    getAgregateurs()
      .then((r) => vivant && r?.agregateurs?.length && setAgregateurs(r.agregateurs))
      .catch(() => {});
    return () => { vivant = false; };
  }, []);

  // Une synthèse par agrégateur, pour les feux du sélecteur. Une synthèse qui tombe
  // éteint son feu (gris, « inconnu »), elle n'empêche pas les autres de s'afficher.
  const chargerSyntheses = useCallback(async () => {
    setErreurSynthese(null);
    const rendus = await Promise.all(agregateurs.map((a) => getSynthese(a.cle)
      .then((s) => [a.cle, s, null]).catch((exc) => [a.cle, null, exc])));
    setSyntheses(Object.fromEntries(rendus.map(([cle, s]) => [cle, s])));
    const echec = rendus.find(([cle]) => cle === agregateur)?.[2];
    if (echec) setErreurSynthese(echec);
  }, [agregateurs, agregateur]);

  const chargerSante = useCallback(async () => {
    setErreurSante(null);
    setErreurDiv(null);
    await Promise.all([
      getSante().then(setSante).catch(setErreurSante),
      getDivergences().then(setDivergences).catch(setErreurDiv),
    ]);
  }, []);

  useEffect(() => { chargerSyntheses(); }, [chargerSyntheses, generation]);
  useEffect(() => { chargerSante(); }, [chargerSante, generation]);

  const apresGeste = useCallback(() => {
    chargerSyntheses();
    chargerSante();
  }, [chargerSyntheses, chargerSante]);

  const toutRelire = () => setGeneration((g) => g + 1);

  const label = agregateurs.find((a) => a.cle === agregateur)?.label || agregateur;
  const synthese = syntheses[agregateur];
  const alertes = alertesSante(sante, divergences, agregateur).total;
  const compte = (n) => (n > 0 ? <span className="ml-1.5 tabular-nums text-neutral-500">{n}</span> : null);

  return (
    <div className="mx-auto max-w-[1280px] space-y-5 p-4 md:p-6" data-testid="page-bridge">
      <Breadcrumb items={[{ label: 'Agrégateurs', to: '/agregateurs' }, { label: 'Bridge' }]} />

      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-[24px] font-semibold tracking-tight text-neutral-900">Bridge agrégateurs</h1>
          <p className="mt-1 max-w-[68ch] text-[13.5px] leading-relaxed text-neutral-600">
            Nous gardons les clés du matériel. L&apos;agrégateur envoie un ordre, nous
            l&apos;exécutons et lui renvoyons le statut et la mesure, sur les seuls actifs
            qui lui sont déclarés.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={toutRelire} aria-label="Tout relire"
                data-testid="bridge-relire">
          <RefreshCcw className="mr-1.5 h-4 w-4" aria-hidden /> Relire
        </Button>
      </header>

      <SelecteurAgregateur agregateurs={agregateurs} actif={agregateur} syntheses={syntheses}
                           surChoix={setAgregateur} />

      <BandeauMigration key={`migration-${generation}`} surMigration={toutRelire} />

      <VerdictRaccordement synthese={synthese} erreur={erreurSynthese} agregateur={agregateur}
                           label={label} surOnglet={setOnglet} />

      <Tabs value={onglet} onValueChange={setOnglet} key={`${agregateur}-${generation}`}>
        <TabsList className="h-auto flex-wrap justify-start">
          <TabsTrigger value="perimetre" data-testid="onglet-btn-perimetre">
            Périmètre{compte(synthese?.compteurs?.actifs_declares)}
          </TabsTrigger>
          <TabsTrigger value="acces" data-testid="onglet-btn-acces">
            Accès{compte(synthese?.compteurs?.cles)}
          </TabsTrigger>
          <TabsTrigger value="journal" data-testid="onglet-btn-journal">
            Journal{compte(synthese?.compteurs?.ordres_7_jours)}
          </TabsTrigger>
          <TabsTrigger value="contrat" data-testid="onglet-btn-contrat">Contrat</TabsTrigger>
          <TabsTrigger value="sante" data-testid="onglet-btn-sante">
            Santé
            {alertes > 0 && (
              <span className="ml-1.5 rounded bg-amber-100 px-1.5 text-[11.5px] tabular-nums text-amber-800"
                    data-testid="onglet-sante-alertes">{alertes}</span>
            )}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="perimetre" className="pt-2">
          <OngletPerimetre agregateur={agregateur} label={label} surChangement={apresGeste} />
        </TabsContent>
        <TabsContent value="acces" className="pt-2">
          <OngletAcces agregateur={agregateur} label={label} surChangement={apresGeste} />
        </TabsContent>
        <TabsContent value="journal" className="pt-2">
          <OngletJournal agregateur={agregateur} />
        </TabsContent>
        <TabsContent value="contrat" className="pt-2">
          <ContratRaccordement agregateur={agregateur} surEnregistrement={apresGeste} />
        </TabsContent>
        <TabsContent value="sante" className="pt-2">
          <OngletSante agregateur={agregateur} label={label} sante={sante} divergences={divergences}
                       erreurSante={erreurSante} erreurDiv={erreurDiv} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
